import type { PropsWithChildren } from 'react';

import { type BudgetCategoryTypeOption } from '@webapp/components/Budgets/BudgetCategoryTypeSelect/BudgetCategoryTypeSelect'
import { type FilterManagerContextValues } from '@webapp/components/FilterManager/FilterManagerContext'
import { SavedFilterManager } from '@webapp/components/SavedFilterManager'
import { type IBudgetCategoryListViewFilters } from '@webapp/hooks/useBudgetCategoryListFilterManager'
import { type SavedFilterCollectionKeys } from '@webapp/hooks/useSavedFilterManager/types'
import {
  formatToAccountCodeFilter,
  formatToDepartmentWithBranchFilter,
  formatToUserFilter,
} from '@webapp/hooks/useSavedFilterManager/utils'
import { getFullName } from '@webapp/utils/user'

export interface IBudgetCategoryListSavedFilterManagerProps {
  collectionId: keyof Pick<
    SavedFilterCollectionKeys,
    'settings.manage.budget.category' | 'dashboard.budget.category.list'
  >
  filterManager: FilterManagerContextValues<IBudgetCategoryListViewFilters>
  budgetCategoryTypeOptions: BudgetCategoryTypeOption[]
}

export const BudgetCategoryListSavedFilterManager = ({
  collectionId,
  filterManager,
  budgetCategoryTypeOptions,
  children,
}: PropsWithChildren<IBudgetCategoryListSavedFilterManagerProps>) => {
  const allowedFilters: (keyof IBudgetCategoryListViewFilters)[] = [
    'departments',
    'account_codes',
  ]

  if (collectionId === 'settings.manage.budget.category')
    allowedFilters.push('users', 'source_of_truth')

  const decodeFilters = {
    departments: (data) =>
      formatToDepartmentWithBranchFilter('departments', data),
    account_codes: (data) => formatToAccountCodeFilter('account_codes', data),
    users: (data) => formatToUserFilter('users', data),
    source_of_truth: (data) => ({
      key: 'source_of_truth',
      paramValue: data,
      value: budgetCategoryTypeOptions.find((option) => option.value === data),
    }),
  }

  const encodeFilters = {
    departments: (value) => ({
      id: value.id,
      name: value.name,
      branch: value.branch,
    }),
    account_codes: (value) => ({
      id: value.id,
      code: `${value.code}`,
      description: value.description,
    }),
    users: (value) => ({
      id: value.id,
      name: getFullName(value),
      _metadata: {
        firstName: value.firstName,
        lastName: value.lastName,
      },
    }),
    source_of_truth: (value) => (value.value === 'True' ? 'True' : 'False'), // explict check for 'True' b/c source_of_truth can also be an empty string
  }

  return (
    <SavedFilterManager
      collectionId={collectionId}
      filterManager={filterManager}
      allowedFilters={allowedFilters}
      decodeFilters={decodeFilters}
      encodeFilters={encodeFilters}
    >
      {children}
    </SavedFilterManager>
  )
}

BudgetCategoryListSavedFilterManager.displayName =
  'BudgetCategoryListSavedFilterManager'
